import { motion } from "framer-motion";
import { Check, MessageCircle, Sparkles, FileText, Star } from "lucide-react";
import { PACKAGES, pad } from "@/lib/catalog";
import { inr } from "@/lib/api";
import { track } from "@/lib/analytics";
import { waFor } from "@/components/FloatingWhatsApp";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { ConsultNotice } from "@/components/ConsultNotice";
import { PackageCompare } from "@/sections/PackageCompare";

export const Disclosure = ({ testId }) => (
  <p className="mt-10 rounded-2xl border border-white/10 bg-[#0B111F] p-5 text-xs leading-relaxed text-slate-400 sm:text-sm" data-testid={testId}>
    <b className="text-slate-200">Please note:</b> All prices are in Indian Rupees (₹) and cover our setup / service charges. Third-party charges — such as domain registration and renewal, hosting plans, API usage, WhatsApp Business platform, CRM subscriptions and payment gateway fees — are not included unless confirmed in writing, and are billed separately where applicable. We confirm all such charges with you before work starts.
  </p>
);

const WaButton = ({ item, testId, light }) => (
  <a href={waFor(`the ${item.name} (${inr(item.price)})`)} onClick={() => { track("package_enquiry_click", { package: item.name, channel: "whatsapp" }); track("whatsapp_click", { location: "package_card", item: item.name }); }}
    target="_blank" rel="noreferrer" data-testid={testId}
    className={`group flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-sm font-semibold transition-transform hover:scale-[1.02] ${light ? "border border-white/20 text-white hover:border-[#25D366]" : "bg-[#00F0FF] text-[#070B14]"}`}>
    <MessageCircle className="h-4 w-4" /> Get Started on WhatsApp
  </a>
);

const Price = ({ p, big }) => (
  <div className="flex flex-wrap items-end gap-x-3 gap-y-1">
    <span className={`font-mono font-bold tracking-tight text-white ${big ? "text-4xl sm:text-5xl" : "text-3xl"}`} data-testid={`${p.id}-price`}>{inr(p.price)}/-</span>
    {p.mrp && <span className="w-full font-mono text-sm text-slate-400 line-through" data-testid={`${p.id}-mrp`}>{inr(p.mrp)}/-</span>}
  </div>
);

const Soon = () => <span className="rounded-full border border-amber-300/50 bg-amber-300/10 px-3 py-1 font-mono text-[11px] uppercase tracking-wider text-amber-200" data-testid="coming-soon-badge">Coming Soon</span>;

const ConsultBtn = ({ item, onConsult, testId }) => (
  <button onClick={() => { track("package_enquiry_click", { package: item.name, channel: "consultation" }); onConsult(item.name, "package_card"); }} data-testid={testId}
    className="w-full rounded-full bg-white py-3.5 text-sm font-semibold text-[#070B14] transition-transform hover:scale-[1.02]">
    Book Free Consultation
  </button>
);

const PackageCard = ({ p, i, onPay, paying, onConsult }) => (
  <motion.article initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }}
    transition={{ duration: 0.8, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
    className={`relative flex h-full flex-col overflow-hidden rounded-3xl border bg-[#0E1526] p-6 sm:p-8 ${p.popular ? "popular-glow border-[#00F0FF]/50" : "border-white/10"}`}
    data-testid={`package-card-${p.id}`}>
    {p.popular && (
      <div className="-mx-6 -mt-6 mb-6 flex items-center justify-center gap-2 bg-[#00F0FF] py-2 font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-[#070B14] sm:-mx-8 sm:-mt-8" data-testid={`package-${p.id}-popular-ribbon`}>
        <Star className="h-3.5 w-3.5 fill-current" /> Most Popular
      </div>
    )}
    {p.popular && <div className="pointer-events-none absolute -right-20 top-0 h-56 w-56 rounded-full bg-[#00F0FF]/15 blur-3xl" />}
    <div className="relative flex items-start justify-between gap-3">
      <span className="font-mono text-xs text-[#00F0FF]">{pad(i + 1)}</span>
      {p.soon ? <Soon /> : <span className="rounded-full bg-[#00F0FF] px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-wider text-[#070B14]">Available now</span>}
    </div>
    <h3 className="relative mt-5 font-display text-2xl font-bold text-white sm:text-3xl">{p.name}</h3>
    <p className="relative mt-1 text-sm text-slate-300">{p.sub}</p>
    <div className="relative mt-6"><Price p={p} big /></div>
    <ol className="relative mt-6 space-y-2.5 border-t border-white/10 pt-6 text-sm">
      {p.features.map((f, fi) => (
        <li key={f} className="grid grid-cols-[1.75rem_1fr] gap-2 text-slate-200">
          <span className="font-mono text-xs leading-5 text-slate-400">{pad(fi + 1)}</span>
          <span className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#00F0FF]" />{f}</span>
        </li>
      ))}
    </ol>
    <div className="relative mt-auto space-y-3 pt-8">
      <ConsultBtn item={p} onConsult={onConsult} testId={`package-${p.id}-consult-button`} />
      <WaButton item={p} testId={`package-${p.id}-whatsapp-button`} light />
      {!p.soon && (
        <button onClick={() => onPay([p.id], p.name)} disabled={paying} data-testid={`package-${p.id}-pay-button`}
          className="w-full rounded-full border border-white/20 py-3 text-sm text-white transition-colors hover:border-[#00F0FF] disabled:opacity-50">
          Or pay {inr(p.price)} online
        </button>
      )}
    </div>
  </motion.article>
);

export const Packages = ({ onPay, paying, onConsult }) => {
  const [a, b, c, aeo] = PACKAGES;
  return (
    <section id="packages" className="relative py-24 sm:py-32" data-testid="packages-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <Reveal>
          <Eyebrow index="02">All-inclusive packages</Eyebrow>
          <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <h2 className="max-w-3xl font-display text-4xl font-bold leading-[1.02] tracking-tight text-white sm:text-6xl">
              One price. <span className="text-outline">Everything</span> you need to go digital.
            </h2>
            <ConsultNotice testId="packages-consult-notice" />
          </div>
        </Reveal>
        <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {[a, b, c].map((p, i) => <PackageCard key={p.id} p={p} i={i} onPay={onPay} paying={paying} onConsult={onConsult} />)}
        </div>
        <PackageCompare />
        <div className="mt-10 grid gap-5 lg:grid-cols-12">
          <Reveal className="lg:col-span-8">
            <article className="relative flex h-full flex-col overflow-hidden rounded-3xl border border-white/10 bg-[#0E1526] p-6 sm:p-8" data-testid={`package-card-${aeo.id}`}>
              <div className="pointer-events-none absolute -bottom-24 -left-10 h-60 w-60 rounded-full bg-[#00F0FF]/10 blur-3xl" />
              <div className="relative flex items-start justify-between gap-3">
                <span className="font-mono text-xs text-[#00F0FF]">04</span>
                <Soon />
              </div>
              <div className="relative mt-5 grid gap-6 sm:grid-cols-[1fr_auto] sm:items-end">
                <div>
                  <h3 className="flex items-center gap-3 font-display text-2xl font-bold text-white sm:text-3xl"><Sparkles className="h-6 w-6 text-[#00F0FF]" />{aeo.name}</h3>
                  <p className="mt-1 text-sm text-slate-300">{aeo.sub}</p>
                  <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-300">{aeo.desc}</p>
                </div>
                <Price p={aeo} big />
              </div>
              <div className="relative mt-8 grid max-w-xl gap-3 sm:grid-cols-2">
                <ConsultBtn item={aeo} onConsult={onConsult} testId={`package-${aeo.id}-consult-button`} />
                <WaButton item={aeo} testId={`package-${aeo.id}-whatsapp-button`} light />
              </div>
            </article>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-4">
            <article className="flex h-full flex-col rounded-3xl border border-dashed border-[#00F0FF]/40 bg-[#0B111F] p-6 sm:p-8" data-testid="request-quote-card">
              <FileText className="h-6 w-6 text-[#00F0FF]" />
              <h3 className="mt-5 font-display text-2xl font-bold text-white">Need something custom?</h3>
              <p className="mt-2 text-sm text-slate-300">Tell us your requirements and we'll send a tailored quote.</p>
              <button onClick={() => onConsult("Custom requirement / Not sure yet", "request_quote")} data-testid="request-quote-button"
                className="mt-auto w-full rounded-full bg-white py-3.5 text-sm font-semibold text-[#070B14] transition-transform hover:scale-[1.02]">
                Request a Quote
              </button>
            </article>
          </Reveal>
        </div>
        <Disclosure testId="packages-disclosure" />
      </div>
    </section>
  );
};
